import {
  Ellipsis,
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { WorkflowStatusWithOverview } from "@certa/processdetails/src/taskDetail/components/ProcessDetailsSecondaryNavbar/components/WorkflowStatusWithOverview";
import { useGetTaskLaneList } from "@certa/queries/hooks/workflow.hooks";
import type { ModelledAPIResponse } from "@certa/queries/types/globalSearch.types";
import format from "date-fns/format";
import { Fragment, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { ChokidarWFStatusPermissionBasedOnToggle } from "../../ChokidarWFStatusPermissionBasedOnToggle";
import { formatLCData } from "../../LCData";
import { useSearchStore } from "../store/useSearchStore";
import { BreadcrumbPathDisplay } from "./BreadcrumbPathDisplay";
import { OverviewListRow } from "./OverviewListRow";
import { RatingPreview } from "./RatingPreview";
import { resultPreviewStyles as styles } from "./ResultPreview.styles";
import { useGetPreviewData } from "../hooks/useSearchKeyboard";
import {
  ActionButton,
  DataObjectActions,
  ActionsItemsWrapper
} from "./PreviewActions";
import { WorkflowStatus } from "../../WorkflowStatus";
import { useGlobalSearch } from "../hooks/useGlobalSearch";
import { STRONG_TAG_REGEX } from "../utils/searchTransformers";
import { classNames } from "@certa/catalyst/utils/common";
import { Eye } from "@certa/icons/components/Eye";
import { Download } from "@certa/icons/components/Download";
import { fileDownload, FileDownloadTypes } from "../../../utils/fileDownload";
import { getFileNameFromAttachmentUrl } from "../../../utils/file";
import { FilePreviewer } from "../../FilePreviewer/FilePreviewer";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";

const PreviewLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack
      width="50%"
      height="100%"
      padding="12 16"
      gap="8"
      overflowX="hidden"
      overflowY="auto"
      dangerouslySetClassName={classNames(
        styles.previewLayoutWrapper,
        "scrollbar-on-hover"
      )}
    >
      {children}
    </Stack>
  );
};

const PreviewTitle = ({ title }: { title: string }) => {
  return (
    <Typography
      variant={TypographyVariants.TITLE_MD}
      color={TypographyColors.NEUTRAL_800}
    >
      <Ellipsis>{title}</Ellipsis>
    </Typography>
  );
};

const WorkflowPreview = ({
  selectedWorkflow
}: {
  selectedWorkflow: ModelledAPIResponse.WorkflowSearchResult;
}) => {
  const { setIsChildDialogOpen } = useSearchStore();
  const {
    workflowFamilyData: { data: workflowFamilyData }
  } = useGlobalSearch();

  const workflowId = selectedWorkflow.id;
  const workflowFamily = workflowFamilyData?.get(workflowId) || [];
  const intl = useIntl();

  // we don't want to show breadcrumbs for single workflow as we show the name of the workflow in the title
  const breadcrumbs =
    workflowFamily.length === 1
      ? []
      : workflowFamily.map(member => member.name);

  const processedLcData = useMemo(() => {
    const lcData = selectedWorkflow?.formattedLcData || [];

    if (!lcData) return [];
    return lcData
      .map(lcDataItem => ({
        ...lcDataItem,
        formattedValue: formatLCData(lcDataItem)
      }))
      .filter(lcDataItem => lcDataItem.formattedValue !== null)
      .slice(0, 4);
  }, [selectedWorkflow]);

  return (
    <PreviewLayout>
      <div className={styles.previewContent}>
        <PreviewTitle title={selectedWorkflow.name} />
        <BreadcrumbPathDisplay items={breadcrumbs} />

        <ChokidarWFStatusPermissionBasedOnToggle
          processId={selectedWorkflow.id}
        >
          <OverviewListRow
            label={intl.formatMessage({
              id: "savedSearch.workflowStatus",
              defaultMessage: "Status"
            })}
            value={
              <div className={styles.workflowStatus}>
                <WorkflowStatusWithOverview
                  status={selectedWorkflow.status?.label}
                  statusColorCode={selectedWorkflow.status?.colorCode}
                  processId={selectedWorkflow.id}
                  width={120}
                  onDialogOpenChange={setIsChildDialogOpen}
                />
              </div>
            }
          />
        </ChokidarWFStatusPermissionBasedOnToggle>

        {processedLcData.map(lcDataItem => (
          <OverviewListRow
            key={lcDataItem.label}
            label={lcDataItem.label}
            value={
              lcDataItem.format === "rating" ? (
                <RatingPreview value={String(lcDataItem.value)} />
              ) : (
                lcDataItem.formattedValue
              )
            }
            shouldWrapReactNodeWithTypography={lcDataItem.format !== "rating"}
          />
        ))}
      </div>
    </PreviewLayout>
  );
};

type FilePreviewProps = {
  selectedFile: ModelledAPIResponse.FileSearchResult;
  setPreviewModalData: (
    previewModalData: ModelledAPIResponse.FileSearchResult
  ) => void;
};

const FilePreview = ({
  selectedFile,
  setPreviewModalData
}: FilePreviewProps) => {
  const {
    workflowFamilyData: { data: workflowFamilyData }
  } = useGlobalSearch();
  const intl = useIntl();
  const workflowId = selectedFile.workflow.id;
  const workflowFamily = workflowFamilyData?.get(workflowId) || [];
  const breadcrumbs = workflowFamily.map(member => member.name);
  const creationDate = selectedFile.workflow.createdAt;
  const formattedDate = creationDate
    ? format(new Date(creationDate), "MMM d, yyyy")
    : "";
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { data: stepGroups, status: stepGroupsFetchStatus } =
    useGetTaskLaneList(workflowId, {
      enabled: isTooltipOpen // Only fetch when tooltip is open
    });

  const createdLabel = intl.formatMessage({
    id: "workflowReport.created",
    defaultMessage: "Created"
  });
  const fieldText = intl.formatMessage({
    id: "commentsDrawer.fieldText",
    defaultMessage: "Field"
  });
  const loadingText = intl.formatMessage({
    id: "designAI.chatInput.loadingStatusText.loading",
    defaultMessage: "Loading..."
  });
  const currentStep = selectedFile.step;
  // stepgroups has results, in which we have swimlanes, each swimlane has steps
  // we need to find the current step in the swimlane
  const currentSwimlane = stepGroups?.results.find(swimlane =>
    swimlane.steps.find(step => step.id === currentStep.id)
  );

  const tooltipHierarchy = [
    { id: currentSwimlane?.id, name: currentSwimlane?.name },
    { id: currentStep.id, name: currentStep.name },
    { id: selectedFile.id, name: selectedFile.body }
  ];

  const handleDownloadFile = () => {
    analyticsService.track(
      AnalyticsEvents.globalSearchEvents.GLOBAL_SEARCH_PREVIEW_ACTION_CLICK,
      {
        result_type: "file",
        content_type: "download",
        certa_version: "OS3"
      }
    );
    fileDownload({
      downloadType: FileDownloadTypes.NEW_TAB,
      url: selectedFile.url
    });
  };

  const content =
    stepGroupsFetchStatus === "loading" ? (
      loadingText
    ) : (
      <div className={styles.tooltip}>
        {tooltipHierarchy.map((item, index) => (
          <Fragment key={item.id}>
            <Typography
              variant={TypographyVariants.LABEL_SM}
              color={TypographyColors.NEUTRAL_700}
            >
              {item.name}
            </Typography>
            {index !== tooltipHierarchy.length - 1 && (
              <Typography
                variant={TypographyVariants.LABEL_SM}
                color={TypographyColors.NEUTRAL_700}
              >
                &rarr;
              </Typography>
            )}
          </Fragment>
        ))}
      </div>
    );

  const previewLabel = intl.formatMessage({
    id: "globalSearchPreview.preview",
    defaultMessage: "Preview"
  });

  const downloadLabel = intl.formatMessage({
    id: "globalSearchPreview.download",
    defaultMessage: "Download"
  });

  const actionsLabel = intl.formatMessage({
    id: "globalSearchPreview.actions",
    defaultMessage: "Actions"
  });

  const handlePreviewModalOpen = () => {
    analyticsService.track(
      AnalyticsEvents.globalSearchEvents.GLOBAL_SEARCH_PREVIEW_ACTION_CLICK,
      {
        result_type: "file",
        content_type: "preview",
        certa_version: "OS3"
      }
    );
    setPreviewModalData(selectedFile);
  };

  return (
    <PreviewLayout>
      <div className={styles.previewContent}>
        <PreviewTitle title={selectedFile.fileName} />
        <BreadcrumbPathDisplay items={breadcrumbs} />
        <OverviewListRow label={createdLabel} value={formattedDate} />

        <OverviewListRow
          label={fieldText}
          onHover={() => setIsTooltipOpen(true)}
          shouldShowCustomTooltip
          tooltip={content}
          value={selectedFile.body}
        />
      </div>
      <ActionsItemsWrapper ariaLabel={actionsLabel} title={actionsLabel}>
        <ActionButton
          onClick={handlePreviewModalOpen}
          icon={Eye}
          label={previewLabel}
        />
        <ActionButton
          onClick={handleDownloadFile}
          icon={Download}
          label={downloadLabel}
        />
      </ActionsItemsWrapper>
    </PreviewLayout>
  );
};

const FieldPreview = ({
  selectedField
}: {
  selectedField: ModelledAPIResponse.FieldResponseSearchResult;
}) => {
  const {
    workflowFamilyData: { data: workflowFamilyData }
  } = useGlobalSearch();

  const intl = useIntl();
  const workflowId = selectedField.workflow.id;
  const workflowFamily = workflowFamilyData?.get(workflowId) || [];

  // we don't want to show breadcrumbs for single workflow as we show the name of the workflow in the title
  const breadcrumbs =
    workflowFamily.length === 1
      ? []
      : workflowFamily.map(member => member.name);

  const creationDate = selectedField.workflow.createdAt;
  const formattedDate = creationDate
    ? format(new Date(creationDate), "MMM d, yyyy")
    : "";

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { data: stepGroups, status: stepGroupsFetchStatus } =
    useGetTaskLaneList(workflowId, {
      enabled: isTooltipOpen // Only fetch when tooltip is open
    });

  const currentStep = selectedField.step;
  // stepgroups has results, in which we have swimlanes, each swimlane has steps
  // we need to find the current step in the swimlane
  const currentSwimlane = stepGroups?.results.find(swimlane =>
    swimlane.steps.find(step => step.id === currentStep.id)
  );

  const tooltipHierarchy = [
    { id: currentSwimlane?.id, name: currentSwimlane?.name },
    { id: currentStep.id, name: currentStep.name },
    { id: selectedField.id, name: selectedField.body }
  ];

  const loadingText = intl.formatMessage({
    id: "designAI.chatInput.loadingStatusText.loading",
    defaultMessage: "Loading..."
  });

  const content =
    stepGroupsFetchStatus === "loading" ? (
      loadingText
    ) : (
      <div className={styles.tooltip}>
        {tooltipHierarchy.map((item, index) => (
          <Fragment key={item.id}>
            <Typography
              variant={TypographyVariants.LABEL_SM}
              color={TypographyColors.NEUTRAL_700}
            >
              {item.name}
            </Typography>
            {index !== tooltipHierarchy.length - 1 && (
              <Typography
                variant={TypographyVariants.LABEL_SM}
                color={TypographyColors.NEUTRAL_700}
              >
                &rarr;
              </Typography>
            )}
          </Fragment>
        ))}
      </div>
    );

  const fieldNameLabel = intl.formatMessage({
    id: "globalSearchPreview.fieldName",
    defaultMessage: "Field Name"
  });

  const valueLabel = intl.formatMessage({
    id: "globalSearchPreview.fieldValueNew",
    defaultMessage: "Field Value"
  });

  const updatedLabel = intl.formatMessage({
    id: "globalSearchPreview.updatedOn",
    defaultMessage: "Updated on"
  });

  const answeredByLabel = intl.formatMessage({
    id: "globalSearchPreview.answeredBy",
    defaultMessage: "Answered by"
  });

  return (
    <PreviewLayout>
      <div className={styles.previewContent}>
        <PreviewTitle title={selectedField?.workflow?.name} />
        <BreadcrumbPathDisplay items={breadcrumbs} />
        <OverviewListRow
          label={fieldNameLabel}
          value={selectedField.body}
          isHighlightedValue
        />
        <OverviewListRow
          label={valueLabel}
          value={selectedField.answer}
          isHighlightedValue
          onHover={() => setIsTooltipOpen(true)}
          shouldShowCustomTooltip
          tooltip={content}
          shouldShowBorderBottom
        />
        <OverviewListRow label={updatedLabel} value={formattedDate} />
        <OverviewListRow
          label={answeredByLabel}
          value={selectedField.submittedBy}
        />
      </div>
    </PreviewLayout>
  );
};

type DataObjectPreviewProps = {
  selectedDataObject: ModelledAPIResponse.ObjectSearchResult;
};

const DataObjectPreview = ({ selectedDataObject }: DataObjectPreviewProps) => {
  const intl = useIntl();
  const status = selectedDataObject.objectInstance.status;

  const fieldLabel = intl.formatMessage({
    id: "globalSearch.fieldLabel",
    defaultMessage: "Field Label"
  });
  const fieldValueLabel = intl.formatMessage({
    id: "globalSearch.fieldValue",
    defaultMessage: "Field Value"
  });
  const createdLabel = intl.formatMessage({
    id: "globalSearch.created",
    defaultMessage: "Created"
  });
  const typeLabel = intl.formatMessage({
    id: "globalSearch.type",
    defaultMessage: "Type"
  });
  const statusLabel = intl.formatMessage({
    id: "globalSearch.status",
    defaultMessage: "Status"
  });

  const fieldItems = selectedDataObject.fieldValue
    ? [
        {
          label: fieldLabel,
          value: selectedDataObject.fieldLabel
        },
        {
          label: fieldValueLabel,
          value: selectedDataObject.fieldValue.replace(STRONG_TAG_REGEX, "")
        }
      ]
    : null;

  const objectItems = [
    {
      label: typeLabel,
      value: selectedDataObject.description
    },
    {
      label: statusLabel,
      value: status ? (
        <WorkflowStatus
          ellipsis={120}
          label={status?.name}
          customColorCode={status?.colorCode}
          size="Medium"
        />
      ) : null
    },
    {
      label: createdLabel,
      value: selectedDataObject.createdAt
        ? format(new Date(selectedDataObject.createdAt), "MMM d, yyyy")
        : null
    }
  ];

  return (
    <PreviewLayout>
      <div className={styles.previewContent}>
        <PreviewTitle title={selectedDataObject.name} />
        {fieldItems?.map((fieldItem, index) => (
          <OverviewListRow
            key={fieldItem.label}
            label={fieldItem.label}
            value={fieldItem.value}
            shouldShowBorderBottom={index === fieldItems.length - 1}
            isHighlightedValue
          />
        ))}
        {objectItems.map(objectInfo =>
          objectInfo.value ? (
            <OverviewListRow
              key={objectInfo.label}
              label={objectInfo.label}
              value={objectInfo.value}
            />
          ) : null
        )}
      </div>
      <DataObjectActions
        objectInstance={selectedDataObject.objectInstance}
        objectType={selectedDataObject.objectType}
      />
    </PreviewLayout>
  );
};

const ResultPreview = () => {
  const { selectedItem, hasSearchQuery, selectedResultType } = useSearchStore();
  const allSearchResults = useGlobalSearch();
  const [selectedFileForPreview, setSelectedFileForPreview] =
    useState<ModelledAPIResponse.FileSearchResult | null>(null);

  const previewData = useGetPreviewData(allSearchResults);

  const selectedResult = selectedResultType
    ? allSearchResults?.[selectedResultType]?.flatData?.[selectedItem]
    : previewData.allData[selectedItem];

  if (!selectedResult || !selectedResult.type || !hasSearchQuery) {
    return null;
  }

  const handlePreviewModalClose = () => {
    setSelectedFileForPreview(null);
  };

  const renderPreviewComponent = () => {
    switch (selectedResult.type) {
      case "workflow":
        return <WorkflowPreview selectedWorkflow={selectedResult} />;
      case "file":
        return (
          <FilePreview
            selectedFile={selectedResult}
            setPreviewModalData={setSelectedFileForPreview}
          />
        );
      case "field":
        return <FieldPreview selectedField={selectedResult} />;
      case "object":
        return <DataObjectPreview selectedDataObject={selectedResult} />;
      default:
        return null;
    }
  };

  return (
    <>
      {renderPreviewComponent()}
      {selectedFileForPreview && (
        <FilePreviewer
          fileName={getFileNameFromAttachmentUrl(selectedFileForPreview.url)}
          visible={!!selectedFileForPreview}
          url={selectedFileForPreview.url}
          closeModal={handlePreviewModalClose}
        />
      )}
    </>
  );
};

export { ResultPreview };
